import React, { useState, useEffect, useCallback, useMemo } from 'react';
import WindowPreco from './windows/WindowPreco';
import WindowEstoque from './windows/WindowEstoque';
import WindowHistorico from './windows/WindowHistorico';
import WindowTransito from './windows/WindowTransito';
import WindowNotas from './windows/WindowNotas';
import WindowCliente from './windows/WindowCliente';
import WindowSimulador from './windows/WindowSimulador';
import WindowPosicaoVendas from './windows/WindowPosicaoVendas';
import WindowDevolucao from './windows/WindowDevolucao';
import WindowCaixasPendentes from './windows/WindowCaixasPendentes';
import WindowTransferenciasPendentes from './windows/WindowTransferenciasPendentes';
import WindowRecebeProtocolo from './windows/WindowRecebeProtocolo';
import WindowConfereCaixa from './windows/WindowConfereCaixa';
import WindowClienteRetira from './windows/WindowClienteRetira';
import WindowInventarioFoto from './windows/WindowInventarioFoto';
import WindowCupons from './windows/WindowCupons';
import WindowPosicaoEstoques from './windows/WindowPosicaoEstoques';
import WindowMovimentacaoEstoque from './windows/WindowMovimentacaoEstoque';
import WindowTransferenciaMercadoria from './windows/WindowTransferenciaMercadoria';
import WindowCadCliente from './windows/WindowCadCliente';
import WindowCadFuncionario from './windows/WindowCadFuncionario';
import WindowCadProduto from './windows/WindowCadProduto';
import WindowReimprimirCupom from './windows/WindowReimprimirCupom';
import WindowBuscaProtocolo from './windows/WindowBuscaProtocolo';
import WindowCriaProtocolo from './windows/WindowCriaProtocolo';
import WindowPedidosEmitidos from './windows/WindowPedidosEmitidos';
import StatusLojaAlerts from './StatusLojaAlerts';
import Avatar from './Avatar';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { authService, lojasService } from '../services/api';
import { usuarioTemPosicaoEstoquesRestrita } from '../utils/loginAccess';
import ComparativoBalanco from '../pages/ComparativoBalanco';
import PreContagemBalanco from '../pages/PreContagemBalanco';
import PosicaoEstoques from '../pages/PosicaoEstoques';
import Avaliacoes from '../pages/Avaliacoes';
import '../styles/ConsultaGlobal.css';

const PERMISSAO_ESTOQUE = 2;
const PERMISSAO_TOTAL = 15;
const STOCK_WINDOWS = new Set(['estoque', 'posicaoEstoques', 'movimentacaoEstoque']);
const FULL_WINDOWS = new Set([
  'preco',
  'historico',
  'posicao',
  'transito',
  'notas',
  'cliente',
  'simulador',
  'devolucao',
  'caixas',
  'transferencias',
  'transferenciaMercadoria',
  'cadCliente',
  'cadFuncionario',
  'cadProduto',
  'reimprimirCupom',
  'recebeProtocolo',
  'confereCaixa',
  'clienteRetira',
  'inventarioFoto',
  'cupons',
  'buscaProtocolo',
  'criaProtocolo',
  'pedidosEmitidos'
]);

const CATEGORIAS_ATALHOS = [
  {
    id: 'todos',
    label: 'Todas as telas',
    icon: 'fa-grip',
    description: 'Todos os recursos disponíveis'
  },
  {
    id: 'consultas',
    label: 'Consultas',
    icon: 'fa-magnifying-glass',
    description: 'Preço, estoque, clientes e documentos'
  },
  {
    id: 'estoque',
    label: 'Estoque e balanço',
    icon: 'fa-boxes-stacked',
    description: 'Posição, movimentação e inventário'
  },
  {
    id: 'protocolos',
    label: 'Protocolos',
    icon: 'fa-file-signature',
    description: 'Criação, consulta e recebimento'
  },
  {
    id: 'operacoes',
    label: 'Operações de loja',
    icon: 'fa-store',
    description: 'Caixas, transferências e conferências'
  },
  {
    id: 'vendas',
    label: 'Vendas e pós-venda',
    icon: 'fa-cart-shopping',
    description: 'Vendas, cupons e devoluções'
  },
  {
    id: 'cadastros',
    label: 'Cadastros',
    icon: 'fa-address-card',
    description: 'Clientes, funcionários e produtos'
  }
];

const CATEGORIA_POR_ATALHO = {
  preco: 'consultas',
  estoque: 'consultas',
  historico: 'consultas',
  cliente: 'consultas',
  notas: 'consultas',
  posicaoEstoques: 'estoque',
  movimentacaoEstoque: 'estoque',
  inventarioFoto: 'estoque',
  preContagemBalanco: 'estoque',
  comparativoBalanco: 'estoque',
  criaProtocolo: 'protocolos',
  pedidosEmitidos: 'protocolos',
  buscaProtocolo: 'protocolos',
  recebeProtocolo: 'protocolos',
  transito: 'protocolos',
  caixas: 'operacoes',
  transferencias: 'operacoes',
  transferenciaMercadoria: 'operacoes',
  confereCaixa: 'operacoes',
  clienteRetira: 'operacoes',
  posicao: 'vendas',
  simulador: 'vendas',
  devolucao: 'vendas',
  reimprimirCupom: 'vendas',
  cupons: 'vendas',
  avaliacoes: 'vendas',
  cadCliente: 'cadastros',
  cadFuncionario: 'cadastros',
  cadProduto: 'cadastros'
};

const normalizarBuscaTela = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const correspondeBuscaTela = (shortcut, termo) => {
  const textoBusca = normalizarBuscaTela([
    shortcut.title,
    shortcut.description,
    shortcut.keywords
  ].filter(Boolean).join(' '));
  const partes = normalizarBuscaTela(termo).split(/\s+/).filter(Boolean);
  return partes.length === 0 || partes.every((parte) => textoBusca.includes(parte));
};

const ConsultaGlobal = ({ userData: userDataProp }) => {
  const [activeWindows, setActiveWindows] = useState([]);
  const [minimizedWindows, setMinimizedWindows] = useState([]);
  const [windowsZIndex, setWindowsZIndex] = useState({});
  const [maxZIndex, setMaxZIndex] = useState(1000);
  const [desktopActive, setDesktopActive] = useState(false);
  const [estoqueAutoSearch, setEstoqueAutoSearch] = useState(null);
  const [estoqueReadOnly, setEstoqueReadOnly] = useState(false);
  const [userData, setUserData] = useState(userDataProp);
  const [buscaTela, setBuscaTela] = useState('');
  const [categoriaAtiva, setCategoriaAtiva] = useState('todos');
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const permissaoBruta = userData?.permissao ?? userData?.PERMISSAO;
  const permissaoParseada = Number.parseInt(permissaoBruta, 10);
  const permissaoUsuario = Number.isFinite(permissaoParseada) ? permissaoParseada : PERMISSAO_TOTAL;
  const posicaoEstoquesRestrita = usuarioTemPosicaoEstoquesRestrita(userData);
  const podeVerTudo = !posicaoEstoquesRestrita && permissaoUsuario === PERMISSAO_TOTAL;
  const podeVerEstoque = podeVerTudo || permissaoUsuario === PERMISSAO_ESTOQUE || posicaoEstoquesRestrita;
  const podeVerMovimentacaoEstoque = !posicaoEstoquesRestrita && podeVerEstoque;
  const podeVerPosicaoEstoques = podeVerEstoque || posicaoEstoquesRestrita;

  const canAccessWindow = useCallback((windowId) => {
    if (windowId === 'posicaoEstoques') return podeVerPosicaoEstoques;
    if (windowId === 'estoque') return podeVerEstoque;
    if (windowId === 'movimentacaoEstoque') return podeVerMovimentacaoEstoque;
    if (STOCK_WINDOWS.has(windowId)) return podeVerEstoque;
    if (FULL_WINDOWS.has(windowId)) return podeVerTudo;
    return false;
  }, [podeVerEstoque, podeVerMovimentacaoEstoque, podeVerPosicaoEstoques, podeVerTudo]);

  // Debug userData e buscar nome da loja se não tiver
  useEffect(() => {
    console.log('👤 UserData:', userData);

    // Se tiver loja_id mas não tiver loja_nome, buscar
    if (userData.loja_id && !userData.loja_nome) {
      lojasService.getLojas().then(lojas => {
        const loja = lojas.find(l => l.id === userData.loja_id || l.numero === userData.loja_id);
        if (loja) {
          // Atualizar userData com nome da loja
          setUserData(prev => ({ ...prev, loja_nome: loja.nome }));
          console.log('Loja encontrada:', loja.nome);
        }
      }).catch(err => console.error('Erro ao buscar lojas:', err));
    }
  }, [userData]);

  // Função para abrir janela
  const openWindow = useCallback((windowId) => {
    if (!canAccessWindow(windowId)) {
      return;
    }

    setDesktopActive(true);

    if (!activeWindows.includes(windowId)) {
      setActiveWindows(prev => [...prev, windowId]);
    }

    // Trazer janela para frente
    setMaxZIndex(prev => prev + 1);
    setWindowsZIndex(prev => ({
      ...prev,
      [windowId]: maxZIndex + 1
    }));
  }, [activeWindows, canAccessWindow, maxZIndex]);

  // Função para fechar janela
  const closeWindow = useCallback((windowId) => {
    setActiveWindows(prev => prev.filter(id => id !== windowId));
  }, []);

  // Função para trazer janela para frente
  const bringToFront = useCallback((windowId) => {
    setMaxZIndex(prev => prev + 1);
    setWindowsZIndex(prev => ({
      ...prev,
      [windowId]: maxZIndex + 1
    }));
  }, [maxZIndex]);

  // Função para minimizar janela
  const minimizeWindow = useCallback((windowId) => {
    setMinimizedWindows(prev => [...prev, windowId]);
  }, []);

  // Função para restaurar janela
  const restoreWindow = useCallback((windowId) => {
    setMinimizedWindows(prev => prev.filter(id => id !== windowId));
    bringToFront(windowId);
  }, [bringToFront]);

  // Função para fechar desktop
  const closeDesktop = useCallback(() => {
    setDesktopActive(false);
    setActiveWindows([]);
    setMinimizedWindows([]);
  }, []);

  // Ao sair da home, garantir que não fica "desktop" aberto por trás
  useEffect(() => {
    if (!isHome && desktopActive) {
      closeDesktop();
    }
  }, [isHome, desktopActive, closeDesktop]);

  useEffect(() => {
    setActiveWindows(prev => prev.filter((windowId) => canAccessWindow(windowId)));
    setMinimizedWindows(prev => prev.filter((windowId) => canAccessWindow(windowId)));
  }, [canAccessWindow]);

  useEffect(() => {
    if (location.pathname === '/comparativo-balanco' && !podeVerTudo) {
      navigate('/', { replace: true });
      return;
    }
    if (location.pathname === '/pre-contagem-balanco' && !podeVerTudo) {
      navigate('/', { replace: true });
      return;
    }
    if (location.pathname === '/posicao-estoques' && !podeVerPosicaoEstoques) {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate, podeVerPosicaoEstoques, podeVerTudo]);

  // Função para logout
  const handleLogout = async () => {
    try {
      await authService.logout();
      window.location.reload();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
    }
  };

  // Atalhos de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      // F5 - Consulta de Preço
      if (e.key === 'F5' && podeVerTudo) {
        e.preventDefault();
        openWindow('preco');
      }
      // F4 - Consulta de Estoque
      else if (e.key === 'F4' && podeVerEstoque) {
        e.preventDefault();
        setEstoqueReadOnly(false); // F4 sempre abre no modo normal (com campo)
        setEstoqueAutoSearch(null); // Não faz auto-search
        openWindow('estoque');
      }
      else if (e.key === 'F6' && podeVerPosicaoEstoques) {
        e.preventDefault();
        closeDesktop();
        openWindow('posicaoEstoques');
      }
      // F7 - Posição de Vendas
      else if (e.key === 'F7' && podeVerTudo) {
        e.preventDefault();
        openWindow('posicao');
      }
      // F8 - Em Trânsito
      else if (e.key === 'F8' && podeVerTudo) {
        e.preventDefault();
        openWindow('transito');
      }
      // F9 - Histórico
      else if (e.key === 'F9' && podeVerTudo) {
        e.preventDefault();
        openWindow('historico');
      }
      // F10 - Caixas Pendentes
      else if (e.key === 'F10' && podeVerTudo) {
        e.preventDefault();
        openWindow('caixas');
      }
      // F11 - Transferencias Pendentes
      else if (e.key === 'F11' && podeVerTudo) {
        e.preventDefault();
        openWindow('transferencias');
      }
      // F12 - Receber Protocolo
      else if (e.key === 'F12' && podeVerTudo) {
        e.preventDefault();
        openWindow('recebeProtocolo');
      }
      // F3 - Cliente Retira
      else if (e.key === 'F3' && podeVerTudo) {
        e.preventDefault();
        openWindow('clienteRetira');
      }
      // ESC - Fechar desktop
      else if (e.key === 'Escape' && desktopActive) {
        e.preventDefault();
        closeDesktop();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeDesktop, desktopActive, navigate, openWindow, podeVerEstoque, podeVerPosicaoEstoques, podeVerTudo]);

  const irParaComparativo = useCallback(() => {
    if (!podeVerTudo) {
      return;
    }
    closeDesktop();
    navigate('/comparativo-balanco');
  }, [closeDesktop, navigate, podeVerTudo]);

  const irParaAvaliacoes = useCallback(() => {
    if (!podeVerTudo) {
      return;
    }
    closeDesktop();
    navigate('/avaliacoes');
  }, [closeDesktop, navigate, podeVerTudo]);

  const atalhosPermitidos = useMemo(() => {
    const atalhos = [];

    if (podeVerEstoque) {
      atalhos.push({
        id: 'estoque',
        icon: 'fa-warehouse',
        title: 'Consultar Estoque',
        description: 'Tecla: F4',
        keywords: 'estoque produto filial quantidade saldo deposito',
        onClick: () => {
          setEstoqueReadOnly(false);
          setEstoqueAutoSearch(null);
          openWindow('estoque');
        }
      });
    }

    if (podeVerPosicaoEstoques) {
      atalhos.push({
        id: 'posicaoEstoques',
        icon: 'fa-table-cells-large',
        title: 'Posição de Estoques',
        description: 'Tecla: F6',
        keywords: 'posicao estoque estoques historico vendas demanda ruptura filial',
        onClick: () => openWindow('posicaoEstoques')
      });
    }

    if (podeVerMovimentacaoEstoque) {
      atalhos.push({
        id: 'movimentacaoEstoque',
        icon: 'fa-dolly',
        title: 'Movimentação de Estoque',
        description: 'Entrada / Saída / Ajuste',
        keywords: 'movimentacao movimento estoque entrada saida ajuste kardex scemov',
        onClick: () => openWindow('movimentacaoEstoque')
      });
    }

    if (podeVerTudo) {
      atalhos.push(
        {
          id: 'preco',
          icon: 'fa-tag',
          title: 'Consultar Preço',
          description: 'Tecla: F5',
          keywords: 'preco produto barras codigo valor promocao consulta',
          onClick: () => openWindow('preco')
        },
        {
          id: 'posicao',
          icon: 'fa-chart-line',
          title: 'Posição de Vendas',
          description: 'Tecla: F7',
          keywords: 'posicao vendas venda vendedor produto exportar excel',
          onClick: () => openWindow('posicao')
        },
        {
          id: 'transito',
          icon: 'fa-truck',
          title: 'Mercadorias Em Trânsito',
          description: 'Tecla: F8',
          keywords: 'mercadoria transito protocolo nota fornecedor receber recebimento',
          onClick: () => openWindow('transito')
        },
        {
          id: 'criaProtocolo',
          icon: 'fa-square-plus',
          title: 'Criar Protocolo',
          description: 'Por chave NF-e ou pedido',
          keywords: 'criar novo protocolo chave nfe pedido gerente destino envio',
          onClick: () => openWindow('criaProtocolo')
        },
        {
          id: 'pedidosEmitidos',
          icon: 'fa-file-export',
          title: 'Últimos Pedidos Emitidos',
          description: 'Consultar chave e gerar protocolo',
          keywords: 'ultimos pedidos emitidos emissao chave nfe gerar criar protocolo destino',
          onClick: () => openWindow('pedidosEmitidos')
        },
        {
          id: 'buscaProtocolo',
          icon: 'fa-magnifying-glass',
          title: 'Buscar Protocolo',
          description: 'Por nº, chave, pedido ou produto',
          keywords: 'busca consultar protocolo chave pedido produto nota transferencia',
          onClick: () => openWindow('buscaProtocolo')
        },
        {
          id: 'historico',
          icon: 'fa-history',
          title: 'Histórico Movimentações',
          description: 'Tecla: F9',
          keywords: 'historico movimentacoes movimento produto estoque kardex entrada saida',
          onClick: () => openWindow('historico')
        },
        {
          id: 'notas',
          icon: 'fa-file-invoice',
          title: 'Notas Fiscais Pendentes',
          description: '',
          keywords: 'nota notas fiscais pendentes fornecedor entrada nfe nf',
          onClick: () => openWindow('notas')
        },
        {
          id: 'cliente',
          icon: 'fa-user',
          title: 'Consultar Clientes',
          description: '',
          keywords: 'cliente clientes cpf nome telefone cadastro',
          onClick: () => openWindow('cliente')
        },
        {
          id: 'cadCliente',
          icon: 'fa-user-pen',
          title: 'Alterar Cadastro Cliente',
          description: 'Editar dados do cliente',
          keywords: 'cadastro cliente alterar editar dados cpf telefone endereco',
          onClick: () => openWindow('cadCliente')
        },
        {
          id: 'cadFuncionario',
          icon: 'fa-id-badge',
          title: 'Alterar Cadastro Funcionário',
          description: 'Editar dados do funcionário',
          keywords: 'cadastro funcionario alterar editar matricula vendedor usuario',
          onClick: () => openWindow('cadFuncionario')
        },
        {
          id: 'cadProduto',
          icon: 'fa-box',
          title: 'Alterar Cadastro Produto',
          description: 'Editar dados do produto',
          keywords: 'cadastro produto alterar editar barras codigo interno',
          onClick: () => openWindow('cadProduto')
        },
        {
          id: 'reimprimirCupom',
          icon: 'fa-receipt',
          title: 'Reimprimir Cupom',
          description: '2ª via da NFC-e',
          keywords: 'reemprimir reimpressao cupom nfce nota fiscal segunda via',
          onClick: () => openWindow('reimprimirCupom')
        },
        {
          id: 'simulador',
          icon: 'fa-shopping-cart',
          title: 'Simulador de Vendas',
          description: 'Pré-venda',
          keywords: 'simulador vendas venda pre-venda orçamento orcamento carrinho',
          onClick: () => openWindow('simulador')
        },
        {
          id: 'devolucao',
          icon: 'fa-undo-alt',
          title: 'Devoluções',
          description: 'Gerenciar devoluções',
          keywords: 'devolucao devolucoes troca retorno produto cupom',
          onClick: () => openWindow('devolucao')
        },
        {
          id: 'caixas',
          icon: 'fa-box-open',
          title: 'Caixas Pendentes',
          description: 'Tecla: F10',
          keywords: 'caixa caixas pendentes conferencia origem produtos pedido',
          onClick: () => openWindow('caixas')
        },
        {
          id: 'transferencias',
          icon: 'fa-right-left',
          title: 'Transferências Pendentes',
          description: 'Tecla: F11',
          keywords: 'transferencia transferencias pendentes pedidos solicitacoes cliente retira recebimento',
          onClick: () => openWindow('transferencias')
        },
        {
          id: 'transferenciaMercadoria',
          icon: 'fa-truck-ramp-box',
          title: 'Transferir Mercadoria',
          description: 'Origem → Destino',
          keywords: 'transferir mercadoria transferencia origem destino loja produto',
          onClick: () => openWindow('transferenciaMercadoria')
        },
        {
          id: 'recebeProtocolo',
          icon: 'fa-clipboard-check',
          title: 'Receber Protocolo',
          description: 'Tecla: F12',
          keywords: 'receber protocolo recebimento callfarma mercadoria transito',
          onClick: () => openWindow('recebeProtocolo')
        },
        {
          id: 'confereCaixa',
          icon: 'fa-boxes-stacked',
          title: 'Conferir Caixa',
          description: 'Tela dedicada',
          keywords: 'conferir caixa conferencia caixas pendentes callfarma',
          onClick: () => openWindow('confereCaixa')
        },
        {
          id: 'clienteRetira',
          icon: 'fa-hand-holding',
          title: 'Cliente Retira',
          description: 'Tecla: F3',
          keywords: 'cliente retira retirada pedido loja transferencia encomenda',
          onClick: () => openWindow('clienteRetira')
        },
        {
          id: 'inventarioFoto',
          icon: 'fa-camera',
          title: 'Inventário por Foto',
          description: 'Upload + Excel',
          keywords: 'inventario foto imagem upload excel contagem produto',
          onClick: () => openWindow('inventarioFoto')
        },
        {
          id: 'cupons',
          icon: 'fa-receipt',
          title: 'Cupons',
          description: 'Buscar por doc. ou valor',
          keywords: 'cupom cupons documento valor pagamento cliente produto fiscal venda',
          onClick: () => openWindow('cupons')
        },
        {
          id: 'avaliacoes',
          icon: 'fa-face-smile',
          title: 'Avaliações de Atendimento',
          description: 'Carinhas do totem por período',
          keywords: 'avaliacao avaliacoes atendimento nota satisfacao carinha emoji totem autoatendimento pesquisa nps cliente vendedor espontanea',
          onClick: irParaAvaliacoes
        },
        {
          id: 'preContagemBalanco',
          icon: 'fa-barcode',
          title: 'Pré-Contagem Balanço',
          description: 'Bipagem local por endereço',
          keywords: 'pre contagem balanco bipagem endereco inventario csv divergencias',
          onClick: () => {
            closeDesktop();
            navigate('/pre-contagem-balanco');
          }
        },
        {
          id: 'comparativoBalanco',
          icon: 'fa-scale-balanced',
          title: 'Comparativo Balanço',
          description: 'Comparar 2 inventários (EI/SI)',
          keywords: 'comparativo balanco inventario comparar ei si divergencia',
          onClick: irParaComparativo
        }
      );
    }

    return atalhos.map((atalho) => ({
      ...atalho,
      category: CATEGORIA_POR_ATALHO[atalho.id] || 'consultas'
    }));
  }, [closeDesktop, irParaAvaliacoes, irParaComparativo, navigate, openWindow, podeVerEstoque, podeVerMovimentacaoEstoque, podeVerPosicaoEstoques, podeVerTudo]);

  const termoBuscaTela = normalizarBuscaTela(buscaTela);
  const atalhosFiltrados = useMemo(
    () => atalhosPermitidos.filter((atalho) => correspondeBuscaTela(atalho, termoBuscaTela)),
    [atalhosPermitidos, termoBuscaTela]
  );

  const categoriasDisponiveis = useMemo(() => CATEGORIAS_ATALHOS.map((categoria) => ({
    ...categoria,
    total: categoria.id === 'todos'
      ? atalhosPermitidos.length
      : atalhosPermitidos.filter((atalho) => atalho.category === categoria.id).length
  })).filter((categoria) => categoria.id === 'todos' || categoria.total > 0), [atalhosPermitidos]);

  const atalhosVisiveis = useMemo(() => {
    if (termoBuscaTela || categoriaAtiva === 'todos') {
      return atalhosFiltrados;
    }

    return atalhosFiltrados.filter((atalho) => atalho.category === categoriaAtiva);
  }, [atalhosFiltrados, categoriaAtiva, termoBuscaTela]);

  const categoriaSelecionada = CATEGORIAS_ATALHOS.find((categoria) => (
    categoria.id === (termoBuscaTela ? 'todos' : categoriaAtiva)
  )) || CATEGORIAS_ATALHOS[0];

  const selecionarCategoria = (categoriaId) => {
    setCategoriaAtiva(categoriaId);
    setBuscaTela('');
    // A grade de atalhos só existe na home: fora dela, escolher uma categoria
    // significa voltar para lá já filtrado.
    if (!isHome) {
      navigate('/');
    }
  };

  const renderSidebarCategorias = (categoriaAtivaId) => (
    <aside className="consulta-desktop-sidebar" aria-label="Categorias de telas">
      <div className="consulta-sidebar-heading">
        <span>Navegação</span>
        <strong>Categorias</strong>
      </div>
      <nav className="consulta-sidebar-nav">
        {categoriasDisponiveis.map((categoria) => {
          const isActive = categoria.id === categoriaAtivaId;
          return (
            <button
              key={categoria.id}
              type="button"
              className={`consulta-sidebar-item${isActive ? ' active' : ''}`}
              onClick={() => selecionarCategoria(categoria.id)}
              aria-pressed={isActive}
            >
              <span className="consulta-sidebar-item-icon"><i className={`fas ${categoria.icon}`}></i></span>
              <span className="consulta-sidebar-item-label">{categoria.label}</span>
              <span className="consulta-sidebar-count">{categoria.total}</span>
            </button>
          );
        })}
      </nav>
    </aside>
  );

  // Telas de página inteira que mantêm a barra lateral de categorias.
  // É uma função de render (e não um componente declarado aqui dentro): um
  // componente novo a cada render remontaria a página e zeraria o estado dela.
  const renderPaginaComSidebar = (categoria, conteudo) => (
    <div className="consulta-welcome">
      <div className="consulta-home-shell">
        {renderSidebarCategorias(categoria)}
        <section className="consulta-home-main">{conteudo}</section>
      </div>
    </div>
  );

  const HomeContent = () => (
    <div className="consulta-welcome">
      {!podeVerEstoque && !podeVerPosicaoEstoques ? (
        <div className="consulta-access-empty">
          <i className="fas fa-lock"></i>
          <h2>Acesso não liberado</h2>
          <p>Este usuário não possui botões disponíveis no Consulta Global.</p>
        </div>
      ) : (
      <>
      {podeVerTudo && <StatusLojaAlerts lojaId={userData?.loja_id} onOpenWindow={openWindow} />}

      <div className="consulta-home-shell">
        {renderSidebarCategorias(termoBuscaTela ? 'todos' : categoriaAtiva)}

        <section className="consulta-home-main">
          <div className="consulta-category-heading">
            <div>
              <span className="consulta-category-eyebrow">Área de trabalho</span>
              <h2>{termoBuscaTela ? 'Resultados da pesquisa' : categoriaSelecionada.label}</h2>
              <p>{termoBuscaTela ? `Telas encontradas para “${buscaTela.trim()}”` : categoriaSelecionada.description}</p>
            </div>
            <span className="consulta-category-total">{atalhosVisiveis.length} {atalhosVisiveis.length === 1 ? 'tela' : 'telas'}</span>
          </div>

          <div className="consulta-screen-search" role="search">
            <i className="fas fa-magnifying-glass"></i>
            <input
              type="search"
              value={buscaTela}
              onChange={(event) => setBuscaTela(event.target.value)}
              placeholder="Pesquisar telas..."
              aria-label="Pesquisar telas"
            />
            {buscaTela && (
              <button type="button" onClick={() => setBuscaTela('')} aria-label="Limpar pesquisa de telas">
                <i className="fas fa-xmark"></i>
              </button>
            )}
          </div>

          {atalhosVisiveis.length > 0 ? (
            <div className="consulta-shortcuts-grid">
              {atalhosVisiveis.map((atalho) => (
                <div key={atalho.id} className="shortcut-card" onClick={atalho.onClick}>
                  <i className={`fas ${atalho.icon}`}></i>
                  <h3>{atalho.title}</h3>
                  {atalho.description && (
                    <p>{atalho.description.startsWith('Tecla:') ? <>Tecla: <kbd>{atalho.description.replace('Tecla: ', '')}</kbd></> : atalho.description}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="consulta-search-empty">
              <i className="fas fa-search"></i>
              <h2>Nenhuma tela encontrada</h2>
              <p>Tente buscar por outro nome, sem se preocupar com acentos.</p>
            </div>
          )}
        </section>
      </div>
      </>
      )}
    </div>
  );

  return (
    <div className="consulta-global-app">
      {/* Header */}
      <div className="consulta-header">
        <div className="consulta-header-left">
          <img src="/favicon.png" alt="seeLL" className="consulta-header-logo" />
        </div>
        <div className="consulta-header-right">
          <div className="consulta-user-info">
            <Avatar size={36} user={userData} />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginLeft: '10px' }}>
              <span style={{ fontWeight: '600' }}>{userData.nome}</span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>{userData.loja_id} - {userData.loja_nome || 'Loja'}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="consulta-logout-btn">
            <i className="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </div>

      <div className="consulta-main-content">
        <Routes>
          <Route path="/" element={<HomeContent />} />
          <Route path="/comparativo-balanco" element={podeVerTudo ? <ComparativoBalanco userData={userData} /> : <Navigate to="/" replace />} />
          <Route path="/pre-contagem-balanco" element={podeVerTudo ? <PreContagemBalanco userData={userData} /> : <Navigate to="/" replace />} />
          <Route path="/posicao-estoques" element={podeVerPosicaoEstoques ? <PosicaoEstoques userData={userData} /> : <Navigate to="/" replace />} />
          <Route
            path="/avaliacoes"
            element={podeVerTudo
              ? renderPaginaComSidebar('vendas', <Avaliacoes />)
              : <Navigate to="/" replace />}
          />
          <Route path="*" element={<HomeContent />} />
        </Routes>
      </div>

      {/* Desktop de janelas */}
      {isHome && desktopActive && (
        <div className="windows-desktop-container">
          {/* Janelas */}
            {activeWindows.includes('preco') && (
              <WindowPreco
                onClose={() => closeWindow('preco')}
                onMinimize={() => minimizeWindow('preco')}
                isMinimized={minimizedWindows.includes('preco')}
                zIndex={windowsZIndex['preco'] || 1000}
                onFocus={() => bringToFront('preco')}
                userData={userData}
                onOpenEstoque={(codigo) => {
                  setEstoqueAutoSearch(codigo);
                  setEstoqueReadOnly(true); // F6 do WindowPreco abre no modo readOnly
                  openWindow('estoque');
                }}
              />
            )}

            {activeWindows.includes('estoque') && (
              <WindowEstoque
                onClose={() => {
                  closeWindow('estoque');
                  setEstoqueReadOnly(false);
                  setEstoqueAutoSearch(null);
                }}
                onMinimize={() => minimizeWindow('estoque')}
                isMinimized={minimizedWindows.includes('estoque')}
                zIndex={windowsZIndex['estoque'] || 1000}
                onFocus={() => bringToFront('estoque')}
                userData={userData}
                autoSearchCode={estoqueAutoSearch}
                readOnly={estoqueReadOnly}
              />
            )}

            {activeWindows.includes('historico') && (
              <WindowHistorico
                isOpen={!minimizedWindows.includes('historico')}
                onClose={() => closeWindow('historico')}
                onMinimize={() => minimizeWindow('historico')}
                zIndex={windowsZIndex['historico'] || 1000}
                onFocus={() => bringToFront('historico')}
                userData={userData}
              />
            )}

            {activeWindows.includes('posicao') && (
              <WindowPosicaoVendas
                isOpen={!minimizedWindows.includes('posicao')}
                onClose={() => closeWindow('posicao')}
                onMinimize={() => minimizeWindow('posicao')}
                zIndex={windowsZIndex['posicao'] || 1000}
                onFocus={() => bringToFront('posicao')}
                userData={userData}
              />
            )}

            {activeWindows.includes('posicaoEstoques') && (
              <WindowPosicaoEstoques
                isOpen={!minimizedWindows.includes('posicaoEstoques')}
                onClose={() => closeWindow('posicaoEstoques')}
                onMinimize={() => minimizeWindow('posicaoEstoques')}
                zIndex={windowsZIndex['posicaoEstoques'] || 1000}
                onFocus={() => bringToFront('posicaoEstoques')}
                userData={userData}
              />
            )}

            {activeWindows.includes('movimentacaoEstoque') && (
              <WindowMovimentacaoEstoque
                onClose={() => closeWindow('movimentacaoEstoque')}
                onMinimize={() => minimizeWindow('movimentacaoEstoque')}
                isMinimized={minimizedWindows.includes('movimentacaoEstoque')}
                zIndex={windowsZIndex['movimentacaoEstoque'] || 1000}
                onFocus={() => bringToFront('movimentacaoEstoque')}
                userData={userData}
              />
            )}

            {activeWindows.includes('transferenciaMercadoria') && (
              <WindowTransferenciaMercadoria
                onClose={() => closeWindow('transferenciaMercadoria')}
                onMinimize={() => minimizeWindow('transferenciaMercadoria')}
                isMinimized={minimizedWindows.includes('transferenciaMercadoria')}
                zIndex={windowsZIndex['transferenciaMercadoria'] || 1000}
                onFocus={() => bringToFront('transferenciaMercadoria')}
                userData={userData}
              />
            )}

            {activeWindows.includes('cadCliente') && (
              <WindowCadCliente
                onClose={() => closeWindow('cadCliente')}
                onMinimize={() => minimizeWindow('cadCliente')}
                isMinimized={minimizedWindows.includes('cadCliente')}
                zIndex={windowsZIndex['cadCliente'] || 1000}
                onFocus={() => bringToFront('cadCliente')}
              />
            )}

            {activeWindows.includes('cadFuncionario') && (
              <WindowCadFuncionario
                onClose={() => closeWindow('cadFuncionario')}
                onMinimize={() => minimizeWindow('cadFuncionario')}
                isMinimized={minimizedWindows.includes('cadFuncionario')}
                zIndex={windowsZIndex['cadFuncionario'] || 1000}
                onFocus={() => bringToFront('cadFuncionario')}
              />
            )}

            {activeWindows.includes('cadProduto') && (
              <WindowCadProduto
                onClose={() => closeWindow('cadProduto')}
                onMinimize={() => minimizeWindow('cadProduto')}
                isMinimized={minimizedWindows.includes('cadProduto')}
                zIndex={windowsZIndex['cadProduto'] || 1000}
                onFocus={() => bringToFront('cadProduto')}
              />
            )}

            {activeWindows.includes('reimprimirCupom') && (
              <WindowReimprimirCupom
                onClose={() => closeWindow('reimprimirCupom')}
                onMinimize={() => minimizeWindow('reimprimirCupom')}
                isMinimized={minimizedWindows.includes('reimprimirCupom')}
                zIndex={windowsZIndex['reimprimirCupom'] || 1000}
                onFocus={() => bringToFront('reimprimirCupom')}
                userData={userData}
              />
            )}

            {activeWindows.includes('buscaProtocolo') && (
              <WindowBuscaProtocolo
                onClose={() => closeWindow('buscaProtocolo')}
                onMinimize={() => minimizeWindow('buscaProtocolo')}
                isMinimized={minimizedWindows.includes('buscaProtocolo')}
                zIndex={windowsZIndex['buscaProtocolo'] || 1000}
                onFocus={() => bringToFront('buscaProtocolo')}
              />
            )}

            {activeWindows.includes('criaProtocolo') && (
              <WindowCriaProtocolo
                onClose={() => closeWindow('criaProtocolo')}
                onMinimize={() => minimizeWindow('criaProtocolo')}
                isMinimized={minimizedWindows.includes('criaProtocolo')}
                zIndex={windowsZIndex['criaProtocolo'] || 1000}
                onFocus={() => bringToFront('criaProtocolo')}
                userData={userData}
              />
            )}

            {activeWindows.includes('pedidosEmitidos') && (
              <WindowPedidosEmitidos
                onClose={() => closeWindow('pedidosEmitidos')}
                onMinimize={() => minimizeWindow('pedidosEmitidos')}
                isMinimized={minimizedWindows.includes('pedidosEmitidos')}
                zIndex={windowsZIndex['pedidosEmitidos'] || 1000}
                onFocus={() => bringToFront('pedidosEmitidos')}
                userData={userData}
              />
            )}

            {activeWindows.includes('transito') && (
              <WindowTransito
                onClose={() => closeWindow('transito')}
                onMinimize={() => minimizeWindow('transito')}
                isMinimized={minimizedWindows.includes('transito')}
                zIndex={windowsZIndex['transito'] || 1000}
                onFocus={() => bringToFront('transito')}
                userData={userData}
              />
            )}

            {activeWindows.includes('notas') && (
              <WindowNotas
                onClose={() => closeWindow('notas')}
                onMinimize={() => minimizeWindow('notas')}
                isMinimized={minimizedWindows.includes('notas')}
                zIndex={windowsZIndex['notas'] || 1000}
                onFocus={() => bringToFront('notas')}
                userData={userData}
              />
            )}

            {activeWindows.includes('cliente') && (
              <WindowCliente
                onClose={() => closeWindow('cliente')}
                onMinimize={() => minimizeWindow('cliente')}
                isMinimized={minimizedWindows.includes('cliente')}
                zIndex={windowsZIndex['cliente'] || 1000}
                onFocus={() => bringToFront('cliente')}
                userData={userData}
              />
            )}

            {activeWindows.includes('simulador') && (
              <WindowSimulador
                onClose={() => closeWindow('simulador')}
                onMinimize={() => minimizeWindow('simulador')}
                isMinimized={minimizedWindows.includes('simulador')}
                zIndex={windowsZIndex['simulador'] || 1000}
                onFocus={() => bringToFront('simulador')}
                userData={userData}
              />
            )}

            {activeWindows.includes('devolucao') && (
              <WindowDevolucao
                onClose={() => closeWindow('devolucao')}
                onMinimize={() => minimizeWindow('devolucao')}
                isMinimized={minimizedWindows.includes('devolucao')}
                zIndex={windowsZIndex['devolucao'] || 1000}
                onFocus={() => bringToFront('devolucao')}
                userData={userData}
              />
            )}

            {activeWindows.includes('caixas') && (
              <WindowCaixasPendentes
                onClose={() => closeWindow('caixas')}
                onMinimize={() => minimizeWindow('caixas')}
                isMinimized={minimizedWindows.includes('caixas')}
                zIndex={windowsZIndex['caixas'] || 1000}
                onFocus={() => bringToFront('caixas')}
                userData={userData}
              />
            )}

            {activeWindows.includes('transferencias') && (
              <WindowTransferenciasPendentes
                onClose={() => closeWindow('transferencias')}
                onMinimize={() => minimizeWindow('transferencias')}
                isMinimized={minimizedWindows.includes('transferencias')}
                zIndex={windowsZIndex['transferencias'] || 1000}
                onFocus={() => bringToFront('transferencias')}
                userData={userData}
              />
            )}

            {activeWindows.includes('recebeProtocolo') && (
              <WindowRecebeProtocolo
                onClose={() => closeWindow('recebeProtocolo')}
                onMinimize={() => minimizeWindow('recebeProtocolo')}
                isMinimized={minimizedWindows.includes('recebeProtocolo')}
                zIndex={windowsZIndex['recebeProtocolo'] || 1000}
                onFocus={() => bringToFront('recebeProtocolo')}
                userData={userData}
              />
            )}

            {activeWindows.includes('confereCaixa') && (
              <WindowConfereCaixa
                onClose={() => closeWindow('confereCaixa')}
                onMinimize={() => minimizeWindow('confereCaixa')}
                isMinimized={minimizedWindows.includes('confereCaixa')}
                zIndex={windowsZIndex.confereCaixa || 1000}
                onFocus={() => bringToFront('confereCaixa')}
                userData={userData}
              />
            )}

            {activeWindows.includes('clienteRetira') && (
              <WindowClienteRetira
                onClose={() => closeWindow('clienteRetira')}
                onMinimize={() => minimizeWindow('clienteRetira')}
                isMinimized={minimizedWindows.includes('clienteRetira')}
                zIndex={windowsZIndex.clienteRetira || 1000}
                onFocus={() => bringToFront('clienteRetira')}
                userData={userData}
              />
            )}

            {activeWindows.includes('inventarioFoto') && (
              <WindowInventarioFoto
                onClose={() => closeWindow('inventarioFoto')}
                onMinimize={() => minimizeWindow('inventarioFoto')}
                isMinimized={minimizedWindows.includes('inventarioFoto')}
                zIndex={windowsZIndex.inventarioFoto || 1000}
                onFocus={() => bringToFront('inventarioFoto')}
                userData={userData}
              />
            )}

            {activeWindows.includes('cupons') && (
              <WindowCupons
                onClose={() => closeWindow('cupons')}
                onMinimize={() => minimizeWindow('cupons')}
                isMinimized={minimizedWindows.includes('cupons')}
                zIndex={windowsZIndex.cupons || 1000}
                onFocus={() => bringToFront('cupons')}
                userData={userData}
              />
            )}
        </div>
      )}

      {/* Taskbar para janelas minimizadas */}
      {isHome && minimizedWindows.length > 0 && (
        <div className="consulta-taskbar active">
          {minimizedWindows.map(windowId => {
            const windowIcons = {
              preco: 'fa-tag',
              posicao: 'fa-chart-line',
              posicaoEstoques: 'fa-table-cells-large',
              estoque: 'fa-warehouse',
              historico: 'fa-history',
              transito: 'fa-truck',
              notas: 'fa-file-invoice',
              cliente: 'fa-user',
              simulador: 'fa-shopping-cart',
              devolucao: 'fa-undo-alt',
              caixas: 'fa-box-open',
              transferencias: 'fa-right-left',
              criaProtocolo: 'fa-square-plus',
              pedidosEmitidos: 'fa-file-export',
              recebeProtocolo: 'fa-clipboard-check',
              confereCaixa: 'fa-boxes-stacked',
              clienteRetira: 'fa-hand-holding',
              inventarioFoto: 'fa-camera'
            };
            const windowTitles = {
              preco: 'Consultar Preço',
              estoque: 'Consultar Estoque',
              posicao: 'Posição de Vendas',
              posicaoEstoques: 'Posição de Estoques',
              historico: 'Histórico',
              transito: 'Em Trânsito',
              notas: 'Notas Pendentes',
              cliente: 'Consultar Cliente',
              simulador: 'Simulador de Vendas',
              devolucao: 'Devoluções',
              caixas: 'Caixas Pendentes',
              transferencias: 'Transferências Pendentes',
              criaProtocolo: 'Criar Protocolo',
              pedidosEmitidos: 'Pedidos Emitidos',
              recebeProtocolo: 'Receber Protocolo',
              confereCaixa: 'Conferir Caixa',
              clienteRetira: 'Cliente Retira',
              inventarioFoto: 'Inventário por Foto'
            };
            return (
              <div
                key={windowId}
                className="consulta-taskbar-item"
                onClick={() => restoreWindow(windowId)}
              >
                <i className={`fas ${windowIcons[windowId]}`}></i>
                {windowTitles[windowId]}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConsultaGlobal;
